import { Card, Text, Divider } from "@mantine/core";
import { ProductMatchCard } from "./ProductMatchCard";
import { NoMatchCard } from "./NoMatchCard";
import { ViewMode } from "@/types";
import { JobTaskDetailSchema } from "@augmodo/lebron-browser-client";
import { useAppSelector } from "@/store/hooks";
import { RootState } from "@/store";

interface ProductSuggestionsProps {
  questionOneHasAnswer: boolean;
  mode?: ViewMode;
  task: JobTaskDetailSchema;
  productIdsTopN: string[];
  productId: string | null;
  planogramId: string | null;
  noMatchNeeded: boolean;
}
export const ProductSuggestions = (props: ProductSuggestionsProps) => {
  const {
    questionOneHasAnswer,
    mode,
    task,
    productIdsTopN,
    productId,
    planogramId,
    noMatchNeeded,
  } = props;
  const selectedProductId = useAppSelector(
    (state: RootState) => state.oos.selectedProductId,
  );
  const productMatchConfirmed = useAppSelector(
    (state: RootState) => state.oos.productMatchConfirmed,
  );
  const itemDetectionConfirmed = useAppSelector(
    (state: RootState) => state.oos.itemDetectionConfirmed,
  );

  return (
    <Card
      shadow="lg"
      padding="lg"
      radius="md"
      withBorder
      style={{
        overflowY: "auto",
        maxHeight: 650,
        opacity:
          (itemDetectionConfirmed === null || questionOneHasAnswer === false) &&
          productMatchConfirmed === null &&
          mode !== "admin" &&
          mode !== "confirmation-qa" &&
          mode !== "confirmation-qa-feedback" &&
          !noMatchNeeded
            ? 0.3
            : 1,
      }}
    >
      <div className="flex flex-col gap-2">
        <Text
          size="md"
          className="text-gray-700"
          style={{
            textAlign: "left",
            fontWeight: 500,
            paddingLeft: "10px",
          }}
        >
          Product Match Detected
        </Text>
        <Divider style={{ margin: "5px 0 10px 0" }} />

        {/* if a user answers no to question 1 or 2, show no match needed */}
        {noMatchNeeded ? (
          <div className="flex items-center justify-center bg-gray-100 p-4 rounded-md h-[100px] mt-2">
            <Text size="md" className="text-gray-700">
              No Product Match Required
            </Text>
          </div>
        ) : (
          <>
            {/* If product selected in the planogram modal and NOT in product predictions */}
            {mode === "viewer" &&
              !!selectedProductId &&
              !productIdsTopN?.includes(selectedProductId) && (
                <ProductMatchCard
                  productId={selectedProductId}
                  mode={mode}
                  task={task}
                  planogramId={planogramId}
                />
              )}

            {/* "viewer" mode displays all product options */}
            {/* if the task does not have any product predictions, display using the default logic */}
            {mode === "viewer" && !productIdsTopN?.length && (
              <ProductMatchCard
                productId={productId}
                mode={mode}
                task={task}
                topPick={true}
                planogramId={planogramId}
              />
            )}
            {/* other product predictions */}
            {mode === "viewer" &&
              productIdsTopN?.length > 0 &&
              productIdsTopN?.map((topNProductId: string, index: number) => (
                <ProductMatchCard
                  productId={topNProductId}
                  mode={mode}
                  task={task}
                  key={topNProductId}
                  topPick={index === 0}
                  planogramId={planogramId}
                />
              ))}

            {/* admin and qa modes: show both prediction and labeler's selection */}
            {mode !== "viewer" &&
              (() => {
                const predictionId = productId;
                const idFromResult = task.result?.confirmation_product_id;
                const confirmationResult = task.data
                  ?.confirmation_result as any;
                // The labeler's match answer lives in task.result (admin) or
                // task.data.confirmation_result (QA modes) — same as selectionId
                // below. Reading only task.result made QA mode miss Case 1 and
                // render the rejected prediction instead of "No match found".
                const matchConfirmed =
                  typeof task.result?.product_match_confirmed === "boolean"
                    ? task.result?.product_match_confirmed
                    : confirmationResult?.product_match_confirmed;
                // Q3 is only a real answer once Q2 (item detection) is "Yes".
                // When Q2 is unanswered/No the match step is moot, so a stored
                // product_match_confirmed=false is a spurious default — not a
                // genuine "No match found" rejection.
                const q2Confirmed =
                  typeof task.result?.item_detection_confirmed === "boolean"
                    ? task.result?.item_detection_confirmed
                    : confirmationResult?.item_detection_confirmed;
                const idFromConfirmationFlow =
                  confirmationResult?.confirmation_product_id;
                const selectionId =
                  (typeof idFromResult === "string"
                    ? idFromResult
                    : undefined) ||
                  (typeof idFromConfirmationFlow === "string"
                    ? idFromConfirmationFlow
                    : undefined);

                // Case 1: labeler genuinely rejected the match (only reachable
                // when Q2 was answered "Yes"). When Q2 isn't "Yes", fall through
                // to Case 2 so the badge shows UNANSWERED rather than "No match".
                if (matchConfirmed === false && q2Confirmed === true) {
                  return (
                    <>
                      {predictionId && (
                        <ProductMatchCard
                          productId={predictionId}
                          mode={mode}
                          task={task}
                          planogramId={planogramId}
                          roleLabel="Predicted (Rejected)"
                          roleColor="red"
                          hideActionStatus
                        />
                      )}
                      <NoMatchCard mode={mode} selected={true} task={task} />
                    </>
                  );
                }

                // Case 2: match step skipped/unanswered (Q1/Q2 failed)
                // — show prediction only, do not imply it was selected
                if (matchConfirmed !== true && !selectionId) {
                  return predictionId ? (
                    <ProductMatchCard
                      productId={predictionId}
                      mode={mode}
                      task={task}
                      planogramId={planogramId}
                      roleLabel="Predicted"
                      roleColor="gray"
                    />
                  ) : null;
                }

                // Case 3: prediction == selection (or labeler implicitly accepted)
                const effectiveSelection = selectionId || predictionId;
                const isSame =
                  !selectionId || effectiveSelection === predictionId;
                if (isSame) {
                  return (
                    <ProductMatchCard
                      productId={effectiveSelection}
                      mode={mode}
                      task={task}
                      planogramId={planogramId}
                      roleLabel="Predicted & Selected"
                      roleColor="green"
                    />
                  );
                }

                // Case 4: different products
                return (
                  <>
                    <ProductMatchCard
                      productId={predictionId}
                      mode={mode}
                      task={task}
                      planogramId={planogramId}
                      roleLabel="Predicted"
                      roleColor="gray"
                      hideActionStatus
                    />
                    <ProductMatchCard
                      productId={effectiveSelection}
                      mode={mode}
                      task={task}
                      planogramId={planogramId}
                      roleLabel="Labeler Selected"
                      roleColor="blue"
                    />
                  </>
                );
              })()}

            {/* "viewer" mode shows unchecked No Match option */}
            {mode === "viewer" && <NoMatchCard mode={mode} />}
          </>
        )}
      </div>
    </Card>
  );
};
